import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Calendar, Radar, ArrowLeft, TrendingUp } from "lucide-react";
import Header from "@/components/homepage/Header";
import { signalDetail } from "@/services/signalsService";
import { formatArDate, relativeAr } from "@/lib/formatDate";

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-3">
    <dt className="text-xs font-medium text-ink-500">{label}</dt>
    <dd className="col-span-2 text-sm text-ink-900">{value ?? <span className="text-ink-500">غير متوفر</span>}</dd>
  </div>
);

export default function SignalDetailPage() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, err: null, signal: null, related: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await signalDetail(id);
        if (!cancelled) setState({ loading: false, err: null, signal: d.signal, related: d.related || [] });
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: String(e), signal: null, related: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const s = state.signal;
  return (
    <>
      <Header />
      <main className="mx-auto min-h-screen w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <Link
          to="/signals"
          data-testid="signal-detail-back"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-fursa-700 hover:text-fursa-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          العودة إلى الأرشيف
        </Link>

        {state.loading ? (
          <div className="mt-8 rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center text-sm text-ink-500">
            جارٍ تحميل الإشارة…
          </div>
        ) : state.err ? (
          <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            {state.err.includes("404") ? "الإشارة غير موجودة أو غير متاحة للعرض العام." : state.err}
          </div>
        ) : (
          <>
            <article className="mt-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {s.country && (
                  <span className="rounded-full border border-fursa-200 bg-fursa-50 px-2 py-0.5 text-[11px] font-semibold text-fursa-700">
                    {s.country}
                  </span>
                )}
                {s.sector && (
                  <span className="rounded-full border border-fursa-200 bg-fursa-50 px-2 py-0.5 text-[11px] font-semibold text-fursa-700">
                    {s.sector}
                  </span>
                )}
                <span className="rounded-full border border-surface-200 bg-surface-50 px-2 py-0.5 text-[11px] text-ink-500">
                  {s.freshness_label}
                </span>
                <span className="rounded-full border border-surface-200 bg-surface-50 px-2 py-0.5 text-[11px] text-ink-500">
                  {s.public_status}
                </span>
              </div>
              <h1
                data-testid="signal-detail-title"
                className="text-2xl font-semibold leading-snug tracking-tight text-ink-900 sm:text-3xl"
              >
                {s.title}
              </h1>
              {s.summary && (
                <p className="text-base leading-relaxed text-ink-700">{s.summary}</p>
              )}

              {/* Metadata */}
              <div className="rounded-xl border border-surface-200 bg-white p-4 shadow-card">
                <dl className="divide-y divide-surface-100">
                  <Row label="المصدر" value={<span className="inline-flex items-center gap-1"><Radar className="h-3 w-3" strokeWidth={1.75} />{s.source_name}</span>} />
                  {s.source_origin && s.source_origin !== s.country && (
                    <Row label="بلد المصدر" value={<span className="text-ink-500">{s.source_origin}</span>} />
                  )}
                  {s.sector && <Row label="القطاع" value={s.sector} />}
                  <Row label="الحداثة" value={s.freshness_label} />
                  <Row
                    label="تاريخ المصدر"
                    value={s.source_published_at ? formatArDate(s.source_published_at, { withTime: true }) : null}
                  />
                  <Row
                    label="أضيف إلى فرصالوجيا"
                    value={s.fursalogia_added_at ? `${formatArDate(s.fursalogia_added_at, { withTime: true })} · ${relativeAr(s.fursalogia_added_at)}` : null}
                  />
                  {s.source_url && (
                    <Row
                      label="الرابط الأصلي"
                      value={
                        <a
                          dir="ltr"
                          href={s.source_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid="signal-detail-source-link"
                          className="inline-flex items-center gap-1 break-all text-fursa-700 hover:text-fursa-800"
                        >
                          <span className="truncate max-w-md">{s.source_url}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                        </a>
                      }
                    />
                  )}
                </dl>

                {/* Linked opportunity — only when a real relation exists */}
                {s.linked_opportunity && (
                  <Link
                    to={`/opportunities/${s.linked_opportunity.public_id}`}
                    data-testid="signal-detail-linked-opportunity"
                    className="mt-4 inline-flex w-full items-center justify-between gap-2 rounded-lg border border-gold-300 bg-gold-50 px-4 py-3 text-sm font-semibold text-gold-800 transition-colors hover:bg-gold-100"
                  >
                    <span className="inline-flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" strokeWidth={1.75} />
                      عرض الفرصة الاستثمارية: {s.linked_opportunity.title}
                    </span>
                    <ArrowLeft className="h-3.5 w-3.5 rotate-180" strokeWidth={1.75} />
                  </Link>
                )}
              </div>
            </article>

            {/* Related — same source, deterministic (no LLM) */}
            {state.related.length > 0 && (
              <aside className="mt-8">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-ink-500">إشارات ذات صلة</h2>
                <p className="mt-1 text-[11px] text-ink-500">من نفس المصدر، مرتّبة حسب القرب الزمني.</p>
                <ul className="mt-3 space-y-2">
                  {state.related.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/signals/${r.id}`}
                        data-testid={`signal-related-${r.id}`}
                        className="block rounded-lg border border-surface-200 bg-white p-3 text-sm text-ink-800 transition-colors hover:border-fursa-300 hover:text-fursa-700"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 shrink-0 text-ink-500" strokeWidth={1.75} />
                          <span className="text-[10px] text-ink-500">{relativeAr(r.fursalogia_added_at)}</span>
                          <span className="text-[10px] text-ink-500/70">·</span>
                          <span className="line-clamp-1">{r.title}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </>
        )}
      </main>
    </>
  );
}
